import { LogLevel } from '@azure/msal-browser';
import type { Configuration, RedirectRequest, SilentRequest } from '@azure/msal-browser';

/**
 * MSAL configuration for the WarehouseLogix SPA.
 *
 * Every identity value is read from `VITE_ENTRA_*` env — there is NO hard-coded
 * client/app id (per CLAUDE.md we must not bake in any product's app id such as
 * FormuLogix `api://1e25bee5`). The corporate tenant is the authority host, but
 * even the tenant id comes from env so the deployment controls it.
 */

/** Fail fast with a clear message when a required env var is absent at build/runtime. */
function requireEnv(name: keyof ImportMetaEnv, value: string | undefined): string {
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `Missing required environment variable "${name}". ` +
        `The SPA cannot configure MSAL without it — set it in the app .env (or the deploy pipeline).`
    );
  }
  return value;
}

/**
 * All identity config is resolved LAZILY (on first use), never at module import.
 * This lets auth-less apps (e.g. the kiosk PWA, which authenticates by device
 * token + PIN, not Entra) import the shared web-common barrel — which re-exports
 * AuthProvider/useAuth — WITHOUT tripping the env validation. The env is only
 * required once something actually uses MSAL.
 */

let cachedApiScope: string | undefined;
/** API scope requested for bearer tokens sent to the WarehouseLogix API. */
export function getApiScope(): string {
  cachedApiScope ??= requireEnv('VITE_ENTRA_API_SCOPE', import.meta.env.VITE_ENTRA_API_SCOPE);
  return cachedApiScope;
}

let cachedMsalConfig: Configuration | undefined;
/** Build (once) the MSAL configuration from `VITE_ENTRA_*`. */
export function getMsalConfig(): Configuration {
  if (cachedMsalConfig !== undefined) {
    return cachedMsalConfig;
  }
  const tenantId = requireEnv('VITE_ENTRA_TENANT_ID', import.meta.env.VITE_ENTRA_TENANT_ID);
  const clientId = requireEnv('VITE_ENTRA_CLIENT_ID', import.meta.env.VITE_ENTRA_CLIENT_ID);
  // Authority host is fixed to the public Entra cloud; the tenant segment is env-driven.
  const authority = `https://login.microsoftonline.com/${tenantId}`;
  cachedMsalConfig = {
    auth: {
      clientId,
      authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // sessionStorage: the admin console's tokens die with the tab and are
      // not shared across tabs/origins' storage (security review 2026-10-08, L3).
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        // Never log PII (tokens, emails) per the engineering contract.
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii): void => {
          if (containsPii) {
            return;
          }
          if (level === LogLevel.Error) {
            // eslint-disable-next-line no-console
            console.error(`[msal] ${message}`);
          }
        },
      },
    },
  };
  return cachedMsalConfig;
}

/** Interactive login request — asks only for the WarehouseLogix API scope. */
export function getLoginRequest(): RedirectRequest {
  return { scopes: [getApiScope()] };
}

/** Silent/interactive token request for the API scope. */
export function getTokenRequest(): Omit<SilentRequest, 'account'> {
  return { scopes: [getApiScope()] };
}
